// Beat Pack Store
import { create } from 'zustand';
import { BeatPack } from '@types';
import { supabase } from '@config/supabase';
import { useAuthStore } from './useAuthStore';
import { throwIfManuallySuspended } from '@utils/suspension';

const BEATPACKS_CACHE_KEY = 'fyle-beatpacks-cache';
const BEATPACKS_CACHE_STALE = 30 * 60 * 1000; // 30 min – wie Files

/** Synchron: Beatpacks aus sessionStorage hydratisieren (erster Paint ohne Warten). */
function getHydratedBeatPackState(): Partial<Pick<BeatPackStore, 'beatPacks'>> {
  if (typeof window === 'undefined') return {};
  try {
    const user = useAuthStore.getState().user;
    if (!user?.id) return {};
    const raw = sessionStorage.getItem(BEATPACKS_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { userId: string; ts: number; beatPacks: BeatPack[] };
    if (parsed.userId !== user.id || !Array.isArray(parsed.beatPacks) || (Date.now() - parsed.ts) > BEATPACKS_CACHE_STALE)
      return {};
    return { beatPacks: parsed.beatPacks };
  } catch {
    return {};
  }
}

interface BeatPackStore {
  beatPacks: BeatPack[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchBeatPacks: () => Promise<void>;
  createBeatPack: (name: string, description: string, fileIds: string[], isPublic?: boolean, linkExpireDays?: number | null, password?: string, contactOverrides?: { contactEmail?: string | null; instagramUrl?: string | null; beatstarsProfileUrl?: string | null; showContact?: boolean }) => Promise<BeatPack>;
  addFilesToBeatPack: (beatPackId: string, fileIds: string[]) => Promise<{ addedCount: number; skippedCount: number; skippedFileIds: string[] }>;
  updateBeatPack: (id: string, name: string, description: string, fileIds: string[], linkExpireDays?: number | null, password?: string, contactOverrides?: { contactEmail?: string | null; instagramUrl?: string | null; beatstarsProfileUrl?: string | null; showContact?: boolean }) => Promise<void>;
  deleteBeatPack: (id: string) => Promise<void>;
  generateShareLink: (id: string) => Promise<string>;
  togglePublic: (id: string) => Promise<void>;
}

export const useBeatPackStore = create<BeatPackStore>((set, get) => ({
  beatPacks: [],
  isLoading: false,
  error: null,
  ...getHydratedBeatPackState(),

  fetchBeatPacks: async () => {
    const user = useAuthStore.getState().user;
    if (!user) {
      set({ beatPacks: [], isLoading: false });
      return;
    }

    // Sofort aus Cache anzeigen, dann im Hintergrund nachladen
    let restoredFromCache = false;
    if (typeof window !== 'undefined') {
      try {
        const raw = sessionStorage.getItem(BEATPACKS_CACHE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as { userId: string; ts: number; beatPacks: BeatPack[] };
          if (parsed.userId === user.id && Array.isArray(parsed.beatPacks) && (Date.now() - parsed.ts) <= BEATPACKS_CACHE_STALE) {
            set({ beatPacks: parsed.beatPacks });
            restoredFromCache = true;
          }
        }
      } catch (_) {}
    }

    if (!restoredFromCache) set({ isLoading: true, error: null });

    try {
      const { data, error } = await supabase
        .from('beat_packs')
        .select(`
          *,
          beat_pack_files (
            file_id
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const beatPacks: BeatPack[] = (data || []).map(pack => ({
        id: pack.id,
        name: pack.name,
        description: pack.description || '',
        files: pack.beat_pack_files?.map((f: any) => f.file_id) || [],
        createdAt: pack.created_at,
        updatedAt: pack.updated_at,
        shareLink: pack.share_link,
        linkExpiresAt: pack.link_expires_at,
        isPublic: pack.is_public,
        downloads: pack.downloads || 0,
        views: pack.views || 0,
        creator: {
          id: pack.user_id,
          name: user.username, // Use current user's name
        },
        contactEmailOverride: pack.contact_email_override ?? undefined,
        instagramUrlOverride: pack.instagram_url_override ?? undefined,
        beatstarsProfileUrlOverride: pack.beatstars_profile_url_override ?? undefined,
        showContact: (pack as { show_contact?: boolean }).show_contact ?? true,
      }));
      
      set({ beatPacks, isLoading: false });
      if (typeof window !== 'undefined') {
        try {
          sessionStorage.setItem(BEATPACKS_CACHE_KEY, JSON.stringify({ userId: user.id, ts: Date.now(), beatPacks }));
        } catch (_) {}
      }
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  createBeatPack: async (name: string, description: string, fileIds: string[], isPublic: boolean = false, linkExpireDays: number | null = 30, password?: string, contactOverrides?: { contactEmail?: string | null; instagramUrl?: string | null; beatstarsProfileUrl?: string | null }) => {
    set({ isLoading: true, error: null });
    
    try {
      const user = useAuthStore.getState().user;
      if (!user) {
        console.error('❌ Not authenticated!');
        throw new Error('Not authenticated');
      }

      // Check if account is manually suspended (without download access)
      await throwIfManuallySuspended();

      console.log('📝 Creating beat pack...', { name, description, fileIds, isPublic, linkExpireDays, hasPassword: !!password, userId: user.id });

      // Calculate expiration date (null = never expires)
      let expirationDate: string | null = null;
      if (linkExpireDays !== null) {
        const date = new Date();
        date.setDate(date.getDate() + linkExpireDays);
        expirationDate = date.toISOString();
      }

      // Hash password if provided (using bcrypt-like approach)
      let hashedPassword: string | null = null;
      if (password) {
        // Simple hash for now - can be replaced with proper bcrypt later
        const encoder = new TextEncoder();
        const data = encoder.encode(password);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        hashedPassword = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      }

      const insertPayload: any = {
        name,
        description,
        user_id: user.id,
        is_public: isPublic,
        link_expires_at: expirationDate,
        password: hashedPassword,
        downloads: 0,
        views: 0,
      };
      if (contactOverrides) {
        insertPayload.contact_email_override = contactOverrides.contactEmail?.trim() || null;
        insertPayload.instagram_url_override = contactOverrides.instagramUrl?.trim() || null;
        insertPayload.beatstars_profile_url_override = contactOverrides.beatstarsProfileUrl?.trim() || null;
        if ('showContact' in contactOverrides && contactOverrides.showContact !== undefined) insertPayload.show_contact = contactOverrides.showContact;
      }
      const { data: pack, error: packError } = await supabase
        .from('beat_packs')
        .insert(insertPayload)
        .select()
        .single();

      if (packError) {
        console.error('❌ Failed to create beat pack:', packError);
        throw packError;
      }

      console.log('✅ Beat pack created:', pack);

      // Generate share link if public
      if (isPublic) {
        const shareLink = `${window.location.origin}/share/${pack.id}`;
        await supabase
          .from('beat_packs')
          .update({ share_link: shareLink })
          .eq('id', pack.id);
      }

      // Add files to beat pack
      if (fileIds.length > 0) {
        console.log('📤 Adding files to beat pack...');
        const packFiles = fileIds.map(fileId => ({
          beat_pack_id: pack.id,
          file_id: fileId,
        }));

        const { error: filesError } = await supabase
          .from('beat_pack_files')
          .insert(packFiles);

        if (filesError) {
          console.error('❌ Failed to add files:', filesError);
          throw filesError;
        }

        console.log('✅ Files added successfully');
      }

      const newPack: BeatPack = {
        id: pack.id,
        name: pack.name,
        description: pack.description || '',
        files: fileIds,
        createdAt: pack.created_at,
        updatedAt: pack.updated_at,
        linkExpiresAt: pack.link_expires_at,
        isPublic: pack.is_public,
        downloads: pack.downloads || 0,
        views: pack.views || 0,
        creator: {
          id: user.id,
          name: user.username,
        },
      };
      
      set(state => ({
        beatPacks: [newPack, ...state.beatPacks],
        isLoading: false,
      }));
      
      console.log('✅ Beat pack saved to store:', newPack);
      return newPack;
    } catch (error: any) {
      console.error('❌ Create beat pack error:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  addFilesToBeatPack: async (beatPackId: string, fileIds: string[]) => {
    set({ isLoading: true, error: null });

    try {
      await throwIfManuallySuspended();

      const uniqueIncomingFileIds = Array.from(new Set(fileIds.filter(Boolean)));
      if (uniqueIncomingFileIds.length === 0) {
        set({ isLoading: false });
        return { addedCount: 0, skippedCount: 0, skippedFileIds: [] };
      }

      const { data: existingRows, error: existingError } = await supabase
        .from('beat_pack_files')
        .select('file_id')
        .eq('beat_pack_id', beatPackId);

      if (existingError) throw existingError;

      const existingFileIds = new Set((existingRows || []).map((row: { file_id: string }) => row.file_id));
      const skippedFileIds = uniqueIncomingFileIds.filter((id) => existingFileIds.has(id));
      const newFileIds = uniqueIncomingFileIds.filter((id) => !existingFileIds.has(id));

      if (newFileIds.length > 0) {
        const rowsToInsert = newFileIds.map((fileId) => ({
          beat_pack_id: beatPackId,
          file_id: fileId,
        }));

        const { error: insertError } = await supabase
          .from('beat_pack_files')
          .insert(rowsToInsert);

        if (insertError) throw insertError;
      }

      const now = new Date().toISOString();
      set((state) => ({
        beatPacks: state.beatPacks.map((pack) =>
          pack.id === beatPackId
            ? {
                ...pack,
                files: Array.from(new Set([...pack.files, ...newFileIds])),
                updatedAt: now,
              }
            : pack
        ),
        isLoading: false,
      }));

      return {
        addedCount: newFileIds.length,
        skippedCount: skippedFileIds.length,
        skippedFileIds,
      };
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  updateBeatPack: async (id: string, name: string, description: string, fileIds: string[], linkExpireDays: number | null = 30, password?: string, contactOverrides?: { contactEmail?: string | null; instagramUrl?: string | null; beatstarsProfileUrl?: string | null }) => {
    set({ isLoading: true, error: null });
    
    try {
      // Check if account is manually suspended (without download access)
      await throwIfManuallySuspended();
      
      console.log('📝 Updating beat pack...', { id, name, description, fileIds, linkExpireDays, hasPassword: !!password });

      // Calculate new expiration date (null = never expires)
      let expirationDate: string | null = null;
      if (linkExpireDays !== null) {
        const date = new Date();
        date.setDate(date.getDate() + linkExpireDays);
        expirationDate = date.toISOString();
      }

      // Hash password if provided (using bcrypt-like approach)
      let hashedPassword: string | null | undefined = undefined;
      if (password !== undefined) {
        if (password) {
          // Simple hash for now - can be replaced with proper bcrypt later
          const encoder = new TextEncoder();
          const data = encoder.encode(password);
          const hashBuffer = await crypto.subtle.digest('SHA-256', data);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          hashedPassword = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        } else {
          hashedPassword = null; // Clear password
        }
      }

      const updateData: any = {
        name,
        description,
        link_expires_at: expirationDate,
        updated_at: new Date().toISOString(),
      };
      if (password !== undefined) {
        updateData.password = hashedPassword;
      }
      if (contactOverrides) {
        updateData.contact_email_override = contactOverrides.contactEmail?.trim() || null;
        updateData.instagram_url_override = contactOverrides.instagramUrl?.trim() || null;
        updateData.beatstars_profile_url_override = contactOverrides.beatstarsProfileUrl?.trim() || null;
        if ('showContact' in contactOverrides && contactOverrides.showContact !== undefined) updateData.show_contact = contactOverrides.showContact;
      }

      const { error: packError } = await supabase
        .from('beat_packs')
        .update(updateData)
        .eq('id', id);

      if (packError) {
        console.error('❌ Failed to update beat pack:', packError);
        throw packError;
      }

      console.log('✅ Beat pack metadata updated');

      // Delete existing file associations
      const { error: deleteError } = await supabase
        .from('beat_pack_files')
        .delete()
        .eq('beat_pack_id', id);

      if (deleteError) {
        console.error('❌ Failed to delete old files:', deleteError);
        throw deleteError;
      }

      // Add new file associations
      if (fileIds.length > 0) {
        console.log('📤 Adding new files to beat pack...');
        const packFiles = fileIds.map(fileId => ({
          beat_pack_id: id,
          file_id: fileId,
        }));

        const { error: filesError } = await supabase
          .from('beat_pack_files')
          .insert(packFiles);

        if (filesError) {
          console.error('❌ Failed to add new files:', filesError);
          throw filesError;
        }

        console.log('✅ New files added successfully');

        // Wenn Beat Pack bereits öffentlich ist: Neue Files auch öffentlich machen
        const pack = get().beatPacks.find(p => p.id === id);
        if (pack?.isPublic) {
          console.log('🔓 Beat pack is public - making new files public too...');
          
          const { error: publicError } = await supabase
            .from('files')
            .update({ is_public: true })
            .in('id', fileIds);

          if (publicError) {
            console.error('⚠️ Warning: Could not make new files public:', publicError);
            // Nicht fatal - nur warnen
          } else {
            console.log('✅ New files are now public');
          }
        }
      }

      // Update local state
      set(state => ({
        beatPacks: state.beatPacks.map(p =>
          p.id === id
            ? { ...p, name, description, files: fileIds, updatedAt: new Date().toISOString() }
            : p
        ),
        isLoading: false,
      }));

      console.log('✅ Beat pack updated successfully');
    } catch (error: any) {
      console.error('❌ Update beat pack error:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  deleteBeatPack: async (id: string) => {
    try {
      // Check if account is manually suspended (without download access)
      await throwIfManuallySuspended();
      
      // Delete files association first
      const { error: filesError } = await supabase
        .from('beat_pack_files')
        .delete()
        .eq('beat_pack_id', id);

      if (filesError) throw filesError;

      // Delete beat pack
      const { error: packError } = await supabase
        .from('beat_packs')
        .delete()
        .eq('id', id);

      if (packError) throw packError;

      set(state => ({
        beatPacks: state.beatPacks.filter(pack => pack.id !== id),
      }));
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  generateShareLink: async (id: string) => {
    try {
      // Check if account is manually suspended (without download access)
      await throwIfManuallySuspended();
      
      const pack = get().beatPacks.find(p => p.id === id);
      if (!pack) throw new Error('Pack not found');
      
      // Generate share link with pack ID
      const shareLink = `${window.location.origin}/share/${id}`;
      
      // Update pack with share link
      const { error } = await supabase
        .from('beat_packs')
        .update({ share_link: shareLink })
        .eq('id', id);

      if (error) throw error;
      
      set(state => ({
        beatPacks: state.beatPacks.map(p =>
          p.id === id ? { ...p, shareLink } : p
        ),
      }));
      
      return shareLink;
    } catch (error: any) {
      throw error;
    }
  },

  togglePublic: async (id: string) => {
    try {
      // Check if account is manually suspended (without download access)
      await throwIfManuallySuspended();
      
      const pack = get().beatPacks.find(p => p.id === id);
      if (!pack) throw new Error('Pack not found');

      const newIsPublic = !pack.isPublic;

      // Update beat pack
      const { error } = await supabase
        .from('beat_packs')
        .update({ is_public: newIsPublic })
        .eq('id', id);

      if (error) throw error;

      // Wenn Beat Pack öffentlich gemacht wird: Alle Files auch öffentlich machen
      if (newIsPublic && pack.files.length > 0) {
        console.log('🔓 Making all files in beat pack public...', pack.files);
        
        const { error: filesError } = await supabase
          .from('files')
          .update({ is_public: true })
          .in('id', pack.files);

        if (filesError) {
          console.error('⚠️ Warning: Could not make all files public:', filesError);
          // Nicht fatal - nur warnen
        } else {
          console.log('✅ All files in beat pack are now public');
        }
      }

      set(state => ({
        beatPacks: state.beatPacks.map(p =>
          p.id === id ? { ...p, isPublic: newIsPublic } : p
        ),
      }));
    } catch (error: any) {
      throw error;
    }
  },
}));

export function resetBeatPackStoreAfterSignOut(): void {
  useBeatPackStore.setState({ beatPacks: [], isLoading: false, error: null });
}

